// Human-readable inbox event lines ("assigned this task to you", "changed status: A -> B", ...).
import type { ReactNode } from 'react';
import { ArrowRight, AtSign, CircleDot, Flag, MessageSquare, MessageSquareReply, UserRound, CalendarClock, Ban, MessageCircle } from '@/components/ui/icons';
import type { Notification, NotificationKind } from '@shared/types';
import { priorityMeta } from '@shared/constants';
import type { Tables } from '@/lib/store';
import { statusOf } from '@/lib/hierarchy';
import { formatDue } from '@/lib/format';
import { findStatus } from './model';

export function StatusChip({ name, color }: { name: string; color: string }) {
  return (
    <span className="ib-status">
      <span className="ib-status-sq" style={{ background: color }} />
      {name}
    </span>
  );
}

/** Highlights "@Name" tokens for known users; mentions of me get the accent color. */
export function Snippet({ text, db, me }: { text: string; db: Tables; me: string }) {
  const names = Object.values(db.users)
    .map((u) => ({ id: u.id, name: u.name }))
    .filter((u) => u.name)
    .sort((a, b) => b.name.length - a.name.length);
  if (!text.includes('@') || !names.length) return <span className="ib-snippet">{text}</span>;
  const parts: ReactNode[] = [];
  let i = 0;
  let buf = '';
  while (i < text.length) {
    if (text[i] === '@') {
      const hit = names.find((u) => text.startsWith(u.name, i + 1));
      if (hit) {
        if (buf) parts.push(buf);
        buf = '';
        parts.push(
          <span key={i} className={hit.id === me ? 'ib-mention me' : 'ib-mention'}>
            @{hit.name}
          </span>,
        );
        i += hit.name.length + 1;
        continue;
      }
    }
    buf += text[i];
    i++;
  }
  if (buf) parts.push(buf);
  return <span className="ib-snippet">{parts}</span>;
}

export const KIND_ICON: Record<NotificationKind, typeof AtSign> = {
  assigned: UserRound,
  mention: AtSign,
  comment: MessageSquare,
  status: CircleDot,
  due_date: CalendarClock,
  priority: Flag,
  reply: MessageSquareReply,
  blocked: Ban,
  assigned_comment: AtSign,
};

export function kindIcon(n: Notification) {
  if (!n.taskId && n.data?.channelId) return MessageCircle;
  return KIND_ICON[n.kind] ?? CircleDot;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** The text after the actor's name. */
export function describeNotification(db: Tables, n: Notification, me: string): ReactNode {
  const task = n.taskId ? db.tasks[n.taskId] : undefined;
  const snippet = str(n.data?.snippet);
  const verb = (s: string) => <span className="ib-verb">{s}</span>;
  switch (n.kind) {
    case 'assigned':
      return verb('assigned this task to you');
    case 'mention':
      return (
        <>
          {verb(n.data?.channelId && !n.taskId ? 'mentioned you in chat:' : 'mentioned you:')} <Snippet text={snippet} db={db} me={me} />
        </>
      );
    case 'comment':
      return (
        <>
          {verb('commented:')} <Snippet text={snippet} db={db} me={me} />
        </>
      );
    case 'reply':
      return (
        <>
          {verb('replied:')} <Snippet text={snippet} db={db} me={me} />
        </>
      );
    case 'assigned_comment':
      return (
        <>
          {verb('assigned you a comment:')} <Snippet text={snippet} db={db} me={me} />
        </>
      );
    case 'status': {
      const from = findStatus(db, task?.listId, n.data?.from);
      const to = findStatus(db, task?.listId, n.data?.to) ?? (task ? statusOf(db, task) : undefined);
      return (
        <>
          {verb('changed status:')} {from && <StatusChip name={from.name} color={from.color} />}
          {from && <ArrowRight size={13} className="ib-arrow" />}
          {to ? <StatusChip name={to.name} color={to.color} /> : <span className="ib-snippet">updated</span>}
        </>
      );
    }
    case 'due_date': {
      const to = typeof n.data?.to === 'number' ? n.data.to : null;
      return to ? (
        <>
          {verb('changed due date to')} <strong className="ib-strong">{formatDue(to)}</strong>
        </>
      ) : (
        verb('removed the due date')
      );
    }
    case 'priority': {
      const meta = priorityMeta(n.data?.to as 1 | 2 | 3 | 4 | null);
      return meta ? (
        <>
          {verb('set priority to')}{' '}
          <span className="ib-priority">
            <Flag size={12} fill={meta.color} color={meta.color} />
            {meta.label}
          </span>
        </>
      ) : (
        verb('removed the priority')
      );
    }
    case 'blocked': {
      const by = db.tasks[str(n.data?.by)];
      const s = by ? statusOf(db, by) : undefined;
      return (
        <>
          {verb('is blocked by')}{' '}
          {by ? (
            <span className="ib-task-chip">
              <span className="ib-status-sq" style={{ background: s?.color }} />
              <span className="truncate">{by.name}</span>
            </span>
          ) : (
            <span className="ib-snippet">another task</span>
          )}
        </>
      );
    }
    default:
      return verb('updated this task');
  }
}
